import React from "react";
import { Box, Option, Button } from "@mui/joy";
import {
  PlaylistAddIcon,
  AddCircleOutlineIcon,
  RemoveCircleOutlineIcon,
} from "../../../styles/StyledMUI";
import DarkSelect from "../../common/DarkSelect";
import DarkInput from "../../common/DarkInput";
import DarkDatePicker from "../../common/DarkDatePicker";
import NumericInput from "../../common/NumericInput";
import DarkTypography from "../../common/DarkTypography";
import type {
  AttributeOption,
  EquipmentAttributeDefinition,
  EquipmentModel,
  FilterCriteria,
  AttributeType,
} from "../../../api/equipment/Equipment.typed";
import {
  EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL,
  getOperatorOptionsForAttribute,
  getDefaultOperatorForAttribute,
  getInputType,
  isEmptyOperatorSentinel,
  resolveEmptyOperatorSentinel,
  resolveEquipmentListFilterSentinel,
  equipmentAttributeUsesEnumPicklist,
} from "../../../utils/searchHelpers";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";

/** API filter type: use attribute type from model; map "comments" to "string" for backend. */
function getFilterCriteriaType(attrType: AttributeType): FilterCriteria["Type"] {
  if (attrType === "comments") return "string";
  if (attrType === "number" || attrType === "date" || attrType === "list" || attrType === "string")
    return attrType;
  return "string";
}

interface AddCriteriaFormProps {
  selectedModel: EquipmentModel;
  onAddCriteria: (criteria: FilterCriteria) => void;
}

export default function AddCriteriaForm({ selectedModel, onAddCriteria }: AddCriteriaFormProps) {
  const [currentAttrId, setCurrentAttrId] = React.useState<string | null>(null);
  const [currentOperator, setCurrentOperator] = React.useState<FilterCriteria["Operator"]>("=");
  const [currentValue, setCurrentValue] = React.useState<string>("");
  const [relativeMode, setRelativeMode] = React.useState<boolean>(false);
  const [monthsDelta, setMonthsDelta] = React.useState<number>(3);
  const [relativeSign, setRelativeSign] = React.useState<1 | -1>(-1);

  const handleAddCriteria = () => {
    if (!currentAttrId) return;

    const attr = (selectedModel.Attributes || []).find(
      (a: EquipmentAttributeDefinition) => a.ID === currentAttrId,
    );
    if (!attr) return;

    const opSentinel = resolveEmptyOperatorSentinel(currentOperator);
    const valueSentinel = resolveEquipmentListFilterSentinel(currentValue);
    const effectiveTrimmed = valueSentinel ? valueSentinel.value.trim() : currentValue.trim();

    if (!opSentinel && !valueSentinel && !effectiveTrimmed) return;

    let resolvedOperator: FilterCriteria["Operator"];
    let resolvedValue: string;

    if (opSentinel) {
      resolvedOperator = opSentinel.operator;
      resolvedValue = opSentinel.value;
    } else if (valueSentinel) {
      resolvedOperator = valueSentinel.operator;
      resolvedValue = valueSentinel.value;
    } else {
      resolvedOperator = currentOperator;
      resolvedValue = currentValue.trim();
    }

    const criteria: FilterCriteria = {
      AttributeID: currentAttrId,
      Operator: resolvedOperator,
      Value: resolvedValue,
      Type: getFilterCriteriaType(attr.Type as AttributeType),
    };
    onAddCriteria(criteria);

    setCurrentAttrId(null);
    setCurrentOperator("=");
    setCurrentValue("");
    setRelativeMode(false);
    setMonthsDelta(3);
    setRelativeSign(-1);
  };

  const handleAttributeChange = (attrId: string | null) => {
    setCurrentAttrId(attrId);
    setCurrentValue("");
    setRelativeMode(false);
    setMonthsDelta(3);
    setRelativeSign(-1);
    const attr = (selectedModel.Attributes || []).find(
      (a: EquipmentAttributeDefinition) => a.ID === attrId,
    );
    if (attr) {
      setCurrentOperator(getDefaultOperatorForAttribute(attr) as FilterCriteria["Operator"]);
    }
  };

  const renderValueInput = () => {
    if (!currentAttrId) return null;

    const attr = (selectedModel.Attributes || []).find(
      (a: EquipmentAttributeDefinition) => a.ID === currentAttrId,
    )!;

    if (equipmentAttributeUsesEnumPicklist(attr)) {
      return (
        <DarkSelect
          value={currentValue || null}
          onChange={(_, v) => {
            const next = String(v ?? "");
            setCurrentValue(next);
            const mapped = resolveEquipmentListFilterSentinel(next);
            if (mapped) setCurrentOperator(mapped.operator as FilterCriteria["Operator"]);
          }}
          sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}
          placeholder="Sélectionner une valeur"
        >
          <Option value={EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL}>Non renseigné</Option>
          {(attr.Options || []).map((o: AttributeOption) => (
            <Option key={o.ID} value={o.Label}>
              {o.Label}
            </Option>
          ))}
        </DarkSelect>
      );
    }
    if (attr.Type === "number") {
      return (
        <NumericInput
          mode="decimal"
          allowNegative
          value={currentValue}
          onValueChange={(raw) => setCurrentValue(raw)}
          sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}
          placeholder="Entrer une valeur"
        />
      );
    }
    if (attr.Type === ("date" as AttributeType)) {
      if (!relativeMode) {
        return (
          <DarkDatePicker
            value={currentValue}
            onChange={setCurrentValue}
            sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}
            placeholder="YYYY-MM-DD"
          />
        );
      }
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <IconButtonCTA
            ctaVariant={relativeSign === -1 ? "accent" : "neutral"}
            onClick={() => {
              setRelativeSign(-1);
              setCurrentValue(monthsDelta === 0 ? "now" : `now-${Math.abs(monthsDelta)}m`);
            }}
            aria-label="Date relative négative"
            title="Aujourd'hui - N mois"
          >
            <RemoveCircleOutlineIcon />
          </IconButtonCTA>
          <IconButtonCTA
            ctaVariant={relativeSign === 1 ? "accent" : "neutral"}
            onClick={() => {
              setRelativeSign(1);
              setCurrentValue(monthsDelta === 0 ? "now" : `now+${Math.abs(monthsDelta)}m`);
            }}
            aria-label="Date relative positive"
            title="Aujourd'hui + N mois"
          >
            <AddCircleOutlineIcon />
          </IconButtonCTA>
          <NumericInput
            mode="int"
            value={String(monthsDelta)}
            onValueChange={(raw) => {
              const n = Number(raw || 0);
              setMonthsDelta(n);
              const token =
                n === 0 ? "now" : relativeSign < 0 ? `now-${Math.abs(n)}m` : `now+${Math.abs(n)}m`;
              setCurrentValue(token);
            }}
            sx={{ width: 90 }}
            placeholder="Mois"
          />
        </Box>
      );
    }

    return (
      <DarkInput
        type={getInputType(attr.Type)}
        value={currentValue}
        onChange={(e) => setCurrentValue(e.target.value)}
        sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}
        placeholder={attr.Type === ("date" as AttributeType) ? "now±Nm" : "Entrer une valeur"}
      />
    );
  };

  const currentAttr = currentAttrId
    ? (selectedModel.Attributes || []).find(
        (a: EquipmentAttributeDefinition) => a.ID === currentAttrId,
      )
    : undefined;
  const operatorIsEmptySentinel = isEmptyOperatorSentinel(currentOperator);
  const submitDisabled =
    !currentAttrId ||
    (!operatorIsEmptySentinel &&
      resolveEquipmentListFilterSentinel(currentValue) === null &&
      currentValue.trim().length === 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <DarkTypography level="body-sm" colorVariant="muted" sx={{ flexShrink: 0 }}>
          Ajouter critère
        </DarkTypography>
        <DarkSelect
          placeholder="Attribut"
          value={currentAttrId}
          onChange={(_, v) => handleAttributeChange(v as string | null)}
          sx={{ minWidth: 160, flexGrow: 1 }}
        >
          {(selectedModel.Attributes || []).map((a: EquipmentAttributeDefinition) => (
            <Option key={a.ID} value={a.ID}>
              {a.Label}
            </Option>
          ))}
        </DarkSelect>
      </Box>

      {currentAttrId && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
          <DarkSelect
            value={currentOperator}
            onChange={(_, v) => {
              const next = (v as FilterCriteria["Operator"]) || "=";
              setCurrentOperator(next);
              if (isEmptyOperatorSentinel(next as string)) setCurrentValue("");
            }}
            sx={{ minWidth: 100, flexShrink: 0 }}
          >
            {getOperatorOptionsForAttribute(currentAttr).map((op) => (
              <Option key={op.value} value={op.value}>
                {op.label}
              </Option>
            ))}
          </DarkSelect>

          {!operatorIsEmptySentinel && (
            <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex" }}>{renderValueInput()}</Box>
          )}

          <IconButtonCTA
            ctaVariant="success"
            onClick={handleAddCriteria}
            disabled={submitDisabled}
            sx={{
              minWidth: "32px",
              minHeight: "32px",
              flexShrink: 0,
              "&:disabled": {
                color: colorTokens.text.muted,
              },
            }}
            aria-label="Ajouter le critère"
            title="Ajouter le critère"
          >
            <PlaylistAddIcon />
          </IconButtonCTA>

          {!operatorIsEmptySentinel && currentAttr?.Type === "date" && (
            <Button
              variant={relativeMode ? "solid" : "outlined"}
              color={relativeMode ? "warning" : "neutral"}
              size="sm"
              onClick={() => setRelativeMode(!relativeMode)}
              sx={{
                minHeight: "32px",
                fontSize: "12px",
                fontWeight: 600,
                backgroundColor: relativeMode ? colorTokens.accent.primary : "transparent",
                color: relativeMode ? colorTokens.accent.contrastText : colorTokens.text.secondary,
                borderColor: colorTokens.border.default,
                "&:hover": {
                  backgroundColor: relativeMode
                    ? colorTokens.accent.primary
                    : withAlpha(colorTokens.text.primary, 0.08),
                  borderColor: colorTokens.accent.hover,
                },
              }}
            >
              Relatif
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
